<template>
    <AppModal :title="$t('filterSubstitutionModal.modal_title')" ref="modal" @on-hide="hide" size="lg">
        <div class="modal-body">
            <AppButton color="indigo mb-3" @click="startAISubstitution" :disabled="substitutionStarted || checkedFilters.length === 0 || (tokenBalance !== null && tokenBalance <= 0)">
                <i :class="['mr-2', substitutionStarted ? 'icon-spinner2 spinner' : 'icon-magic-wand']"></i>
                {{ $t('filterSubstitutionModal.determine') }}
            </AppButton>
            <Alert v-if="tokenBalance !== null && tokenBalance <= 0" type="warning" class="mb-3">
                {{ $t('alerts.insufficient_ai_balance') }}
            </Alert>
            <div class="table-responsive table-scrollable">
                <table class="table table-bordered table-fixed">
                    <thead>
                        <tr>
                            <th width="60px">
                                <AppCheckbox
                                    :model-value="allValueIsChecked"
                                    @update:model-value="checkAllFilters"
                                />
                            </th>
                            <th width="100px">{{ $t('labels.filter') }}</th>
                            <th width="200px">{{ $t('filterSubstitutionModal.old_filter_item') }}</th>
                            <th width="200px">{{ $t('filterSubstitutionModal.new_filter_item') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(filter, filterIndex) in allFilters">
                            <td>
                                <AppCheckbox
                                    :model-value="checkedFilters.includes(filter.uuid)"
                                    @update:model-value="checkFilter(filter)"
                                />
                            </td>
                            <td>{{ getAvailableTranslations(filter.names) + (filter.measurement_unit ? ` (${filter.measurement_unit})` : '') }}</td>
                            <td>{{ oldFilters[filter.uuid]?.items.map(item => getAvailableTranslations(item.names)).join(', ') }}</td>
                            <td>
                                <InputGroup v-if="!createModes[filter.uuid]">
                                    <CatalogAttributeSelect
                                        :attribute="filter"
                                        :integration="integration"
                                        :category="product.category"
                                        :model-value="selectedFilters[filter.uuid]?.items || []"
                                        @update:model-value="selectFilter(filter, $event)"
                                    />
                                    <template #append>
                                        <div class="d-flex flex-wrap" style="gap: 0.5rem">
                                            <AppButton color="teal" @click="enableCreateMode(filter.uuid)">
                                                <i class="icon-plus3"></i>
                                            </AppButton>
                                            <AppButton 
                                                color="indigo" 
                                                @click="regenerateAttributeValue(filter)" 
                                                :disabled="isRegeneratingAttribute(filter.uuid)"
                                            >
                                                <i class="icon-reset" v-if="!isRegeneratingAttribute(filter.uuid)"></i>
                                                <i class="icon-spinner2 spinner" v-else></i>
                                            </AppButton>
                                        </div>
                                    </template>
                                </InputGroup>
                                <InputGroup v-else>
                                    <Input
                                        :model-value="newFilterItemNames[filter.uuid]"
                                        @update:model-value="(value) => newFilterItemNames[filter.uuid] = value"
                                    />
                                    <template #append>
                                        <div class="d-flex flex-wrap" style="gap: 0.5rem">
                                            <AppButton
                                                color="teal"
                                                :disabled="!filter.allow_custom_values"
                                                @click="createFilterItem(filter.uuid)"
                                            >
                                                <i class="icon-checkmark3"></i>
                                            </AppButton>
                                            <AppButton color="danger" @click="disableCreateMode(filter.uuid)">
                                                <i class="icon-cross2"></i>
                                            </AppButton>
                                        </div>
                                    </template>
                                </InputGroup>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <div class="modal-footer">
            <AppButton color="indigo" @click="$emit('on-substitute', selectedFilters)" :disabled="substitutionStarted || Object.keys(selectedFilters).length === 0">
                {{ $t('buttons.substitute') }}
            </AppButton>
        </div>
    </AppModal>
</template>
<script>
import AppModal from '../../../../../../../components/new/modal/AppModal.vue';
import Alert from '../../../../../../../components/AlertComponent.vue';
import AppCheckbox from '../../../../../../../components/new/form/checkbox/AppCheckbox.vue';
import CatalogAttributeSelect from '../CatalogAttributeSelect.vue';
import AppButton from '../../../../../../../components/new/button/AppButton.vue';
import Input from '../../../../../../../components/new/form/Input.vue';
import InputGroup from '../../../../../../../components/new/form/InputGroup.vue';

import MessageNotificator from '../../../../../../../services/MessageNotificator';
import GeminiService from '../../../../../../../services/GeminiService/GeminiService';
import { httpClient } from '../../../../../../../services/HttpClient';
import { NotValidJsonStringError } from '../../../../../../../services/Errors/NotValidJsonStringError';
import { TooManyRequestsError } from '../../../../../../../services/Errors/TooManyRequestsError';
import { ServiceUnavailableError } from '../../../../../../../services/Errors/ServiceUnavailableError';

export default {
    name: 'FilterSubstitutionModal',
    components: {
        AppModal, Alert, AppCheckbox, CatalogAttributeSelect, AppButton, Input, InputGroup,
    },
    props: {
        getAvailableTranslations: {
            type: Function,
            required: true,
        },
        product: {
            type: Object,
            required: true,
        },
        integration: {
            type: Object,
            required: true,
        },
        mainLanguageAlias: {
            type: String,
            required: true,
        }
    },
    data() {
        return {
            allFilters: [],
            checkedFilters: [],
            selectedFilters: {},
            oldFilters: {},
            substitutionStarted: false,
            createModes: {},
            newFilterItemNames: {},
            regeneratingAttributes: {},
            tokenBalance: null
        };
    },
    methods: {
        async fetchBalance() {
            const response = await httpClient.get(`/projects/${this.$route.params.projectUuid}/core/ai-transactions/balance`);
            this.tokenBalance = response.data.balance;
        },
        enableCreateMode(filterUuid) {
            this.createModes[filterUuid] = true;
            this.newFilterItemNames[filterUuid] = ''; 
        },
        disableCreateMode(filterUuid) {
            this.createModes[filterUuid] = false;
            this.newFilterItemNames[filterUuid] = '';
        },
        isRegeneratingAttribute(filterUuid) {
            return !!this.regeneratingAttributes[filterUuid];
        },
        async createFilterItem(filterUuid) {
            const filterItemName = this.newFilterItemNames[filterUuid];
            if (!filterItemName) return;

            const requestData = {
                integration_filter_uuid: filterUuid,
                names: {
                    [this.mainLanguageAlias]: filterItemName 
                }
            };

            try {
                const response = await httpClient.post(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filter-items`, requestData);
                const newItem = response.data.data;

                const filter = this.allFilters.find(f => f.uuid === filterUuid);
                
                if (filter.is_multiple) {
                    let items = this.selectedFilters[filterUuid]?.items || [];
                    if (!items.find(item => item.uuid === newItem.uuid)) {
                        items.push(newItem);
                    }
                    this.selectedFilters[filterUuid] = { items: items };
                } else {
                    this.selectedFilters[filterUuid] = { items: [newItem] };
                }

                this.disableCreateMode(filterUuid);
            } catch (error) {
                new MessageNotificator().error(this.$t('alerts.contact_support'));
            }
        },
        async regenerateAttributeValue(filter) {
            this.regeneratingAttributes[filter.uuid] = true;
            
            try {
                const filtersWithItems = await this.fetchFilterItemsForFilters({ [filter.uuid]: filter });
                const substitutedItems = await this.processProductWithAI(this.product, filtersWithItems);
                
                if (substitutedItems && substitutedItems[filter.uuid]) {
                    const formattedData = this.formatFilterData(filter.uuid, substitutedItems[filter.uuid], filtersWithItems);
                    if (formattedData) {
                        this.selectedFilters[filter.uuid] = { items: formattedData.items };
                    } else if (filter.allow_custom_values) {
                        this.enableCreateMode(filter.uuid);
                        this.newFilterItemNames[filter.uuid] = substitutedItems[filter.uuid];
                    }
                } else {
                    delete this.selectedFilters[filter.uuid];
                }
            } catch (error) {
                new MessageNotificator().error(this.$t('alerts.contact_support'));
            } finally {
                this.regeneratingAttributes[filter.uuid] = false;
            }
        },
        show(allFilters, selectedFilters) {
            this.allFilters = [...allFilters].filter(filter => filter.default_items.length === 0);
            this.oldFilters = { ...selectedFilters };

            this.fetchBalance();
            this.$refs.modal.show();
        },
        hide() {
            this.$refs.modal.hide();
            this.allFilters = [];
            this.checkedFilters = [];
            this.selectedFilters = {};
            this.oldFilters = {};
            this.newFilterItemNames = {};
            this.createModes = {};
            this.regeneratingAttributes = {};
            this.substitutionStarted = false;
        },
        getSelectedIntegrationFilterItemUuids(filter) {
            if (this.selectedFilters[filter.uuid]) {
                return this.selectedFilters[filter.uuid].items.map(item => item.uuid);
            }

            return [];
        },
        checkFilter(filter) {
            if (this.checkedFilters.includes(filter.uuid)) {
                this.checkedFilters = this.checkedFilters.filter(f => f !== filter.uuid)
            } else {
                this.checkedFilters.push(filter.uuid)
            }
        },
        checkAllFilters() {
            if (this.allValueIsChecked) {
                this.checkedFilters = []
            } else {
                this.checkedFilters = this.allFilters.map(filter => filter.uuid)
            }
        },
        selectFilter(filter, filterItems) {
            const filterUuid = filter.uuid;

            if (!filterItems || filterItems.length === 0) {
                delete this.selectedFilters[filterUuid];
                return;
            }

            this.selectedFilters[filterUuid] = {
                items: filterItems
            };
        },
        async startAISubstitution() {
            this.substitutionStarted = true;

            try {
                // Prepare checked filters for processing
                const selectedFiltersForSubstitution = {};

                this.checkedFilters.forEach(filterUuid => {
                    const filter = this.allFilters.find(f => f.uuid === filterUuid);
                    if (filter) {
                        selectedFiltersForSubstitution[filterUuid] = filter;
                    }
                });

                // Fetch filter items for selected filters
                const filtersWithItems = await this.fetchFilterItemsForFilters(selectedFiltersForSubstitution);
                // Process product with AI
                const substitutedItems = await this.processProductWithAI(this.product, filtersWithItems);
                // Apply results to selectedFilters
                if (substitutedItems) {
                    Object.entries(substitutedItems).forEach(([filterUuid, filterItemValue]) => {
                        const formattedData = this.formatFilterData(filterUuid, filterItemValue, filtersWithItems);
                        if (formattedData) {
                            this.selectedFilters[filterUuid] = {
                                items: formattedData.items
                            };
                        } else {
                            const filter = this.allFilters.find(f => f.uuid === filterUuid);
                            if (filter && filter.allow_custom_values) {
                                this.enableCreateMode(filterUuid);
                                this.newFilterItemNames[filterUuid] = filterItemValue;
                            }
                        }
                    });
                }

            } catch (error) {
                new MessageNotificator().error(this.$t('alerts.contact_support'));
            } finally {
                this.substitutionStarted = false;
            }
        },
        async fetchFilterItemsForFilters(selectedFiltersForSubstitution) {
            const filtersWithItems = {};

            for (const [filterUuid, filter] of Object.entries(selectedFiltersForSubstitution)) {
                filtersWithItems[filterUuid] = {
                    uuid: filter.uuid,
                    name: filter.names[this.mainLanguageAlias] || Object.values(filter.names)[0],
                    type: filter.type,
                    filterItems: [],
                };

                // Fetch filter items
                let filterItemsCount = 0;
                await httpClient.get(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filter-items`, {
                    integration_filter_uuids: [filterUuid],
                    limit: 1
                }).then(response => {
                    filterItemsCount = response.data.meta.total;
                });

                if (filterItemsCount > 0) {
                    for (let i = 0; i < filterItemsCount; i += 50) {
                        await httpClient.get(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filter-items`, {
                            integration_filter_uuids: [filterUuid],
                            limit: 50,
                            page: Math.floor(i / 50) + 1
                        }).then(response => {
                            if (!response.data.status) {
                                response.data.data.forEach(item => {
                                    filtersWithItems[filterUuid].filterItems.push({
                                        uuid: item.uuid,
                                        name: item.names[this.mainLanguageAlias] || Object.values(item.names)[0]
                                    });
                                });
                            }
                        });
                    }
                }
            }

            return filtersWithItems;
        },
        async processProductWithAI(product, filtersWithItems) {
            const geminiService = new GeminiService();

            try {
                const filtersForPrompt = await this.prepareFiltersForPrompt(filtersWithItems, geminiService);

                await geminiService.flushTokens(this.$route.params.projectUuid);
                await this.fetchBalance();

                if (this.tokenBalance !== null && this.tokenBalance <= 0) {
                    return {};
                }

                // Create product data for prompt
                const productForPrompt = {
                    uuid: product.uuid,
                    names: product.names,
                    descriptions: product.descriptions,
                    sku: product.sku,
                    images: product.images || []
                };

                // Try keyword-based search first
                const keywordResults = await this.searchFiltersByKeywords(productForPrompt, filtersForPrompt, geminiService);

                await geminiService.flushTokens(this.$route.params.projectUuid);
                await this.fetchBalance();

                if (this.tokenBalance !== null && this.tokenBalance <= 0) {
                    return keywordResults;
                }

                // Try filter list search for filters with manageable number of items
                const filtersForListSearch = filtersForPrompt.filter(filter =>
                    filtersWithItems[filter.uuid] && filtersWithItems[filter.uuid].filterItems.length <= 300
                );

                if (filtersForListSearch.length > 0) {
                    const listSearchResults = await this.searchFiltersInList(productForPrompt, filtersForListSearch, filtersWithItems, geminiService);

                    await geminiService.flushTokens(this.$route.params.projectUuid);
                    await this.fetchBalance();

                    // Merge results
                    Object.assign(keywordResults, listSearchResults);
                }

                return keywordResults;
            } finally {
                await geminiService.flushTokens(this.$route.params.projectUuid);
            }
        },
        async prepareFiltersForPrompt(filters, geminiService) {
            let filtersForPrompt = Object.values(filters).map(filter => ({
                uuid: filter.uuid,
                name: filter.name,
            }));

            let attributeDefinitionsPrompt = `
                You are given:
                - A product category name.
                - A list of attributes; each attribute has: uuid and name.

                Goal:
                For each attribute, write a short, clear, unambiguous definition tailored to this category.

                Rules:
                1) Disambiguate the intended meaning (e.g., "Compatible brand" = brand of the device the accessory fits, not the accessory manufacturer).
                2) The definition should be concise (1–2 sentences), actionable for information extraction.
                3) Use neutral, domain-appropriate language.
                4) Produce a definition for every input attribute.
                5) Return STRICTLY valid JSON. JSON only, no extra text.

                Input:
                Category names: ${JSON.stringify(this.product.category.names)}
                Attributes: ${JSON.stringify(filtersForPrompt)}

                Expected output (STRICT JSON object):
                {
                  "<attributeUuid>": "Attribute definition string",
                  "<attributeUuid>": "Attribute definition string"
                }
            `;

            let attributeDefinitions = {};

            try {
                attributeDefinitions = await geminiService.generateWithRetry(attributeDefinitionsPrompt);
            } catch (error) {
                if (error instanceof NotValidJsonStringError) {
                    new MessageNotificator().error(this.$t('alerts.received_not_valid_json'));
                } else if (error instanceof TooManyRequestsError) {
                    new MessageNotificator().error(this.$t('alerts.too_many_requests'));
                } else if (error instanceof ServiceUnavailableError) {
                    new MessageNotificator().error(this.$t('alerts.service_unavailable'));
                } else {
                    new MessageNotificator().error(this.$t('alerts.contact_support'));
                }
                return filtersForPrompt;
            }

            return filtersForPrompt.map(filter => ({
                uuid: filter.uuid,
                name: filter.name,
                definition: attributeDefinitions[filter.uuid] ?? null,
            }));
        },
        async searchFiltersByKeywords(product, filtersForPrompt, geminiService) {
            const prompt = `
                You are given:
                1. A product object. The product contains: uuid, name, description, keywords (possibly multilingual), and images.
                2. An array of attribute definitions, each containing: uuid, name, and an optional "definition" string.

                Your task is to determine the most appropriate value for each attribute of the product based on:
                - Semantic understanding of the product's textual data (names, descriptions, keywords, in all available languages).
                - Visual analysis of the product's images (e.g., color, pattern, material, style, category).

                Rules:
                1. Use only the provided list of attributes (uuid + name + optional definition).
                2. If an attribute object contains a non-null "definition", treat it as authoritative and follow it strictly.
                3. If you can confidently determine an attribute value, return it as a human-readable string.
                4. If you cannot determine the value with high confidence, do not include that attribute in the output.
                5. Do not fabricate or generalize values.
                6. The response must be STRICTLY valid JSON.
                7. Keys must be attribute uuids.
                8. Values must be strings containing the most likely attribute value.
                9. No explanations, no additional text — JSON only.

                Input format:
                Product: ${JSON.stringify(product)}
                Attributes: ${JSON.stringify(filtersForPrompt)}

                Expected output format:
                {
                  "<attributeUuid>": "attribute value string",
                  "<attributeUuid>": "attribute value string"
                }
            `;

            try {
                return await geminiService.generateWithRetry(prompt);
            } catch (error) {
                if (error instanceof NotValidJsonStringError) {
                    new MessageNotificator().error(this.$t('alerts.received_not_valid_json'));
                } else if (error instanceof TooManyRequestsError) {
                    new MessageNotificator().error(this.$t('alerts.too_many_requests'));
                } else if (error instanceof ServiceUnavailableError) {
                    new MessageNotificator().error(this.$t('alerts.service_unavailable'));
                } else {
                    new MessageNotificator().error(this.$t('alerts.contact_support'));
                }
                return {};
            }
        },
        async searchFiltersInList(product, filters, filtersWithItems, geminiService) {
            const filtersWithItemsList = filters.map(filter => ({
                ...filter,
                filterItems: filtersWithItems[filter.uuid].filterItems
            }));

            const prompt = `
                You are given a product object and a list of attribute definitions with their possible values.

                Your task is to analyze the product's data and determine the most appropriate value for each attribute from the provided options.

                Attribute values should be selected based on semantic understanding of the product name, description, and keywords (in all available languages) and visual analysis of the product images.

                You must only select from the existing filter items provided. Do not create custom values.

                If you can't determine the value or don't find a match among existing values, don't substitute anything.

                Return the result strictly as a valid JSON object, where each key is the attribute uuid and each value is the selected attribute item uuid. Do not return any explanation or extra text.

                Product: ${JSON.stringify(product)}
                Attributes: ${JSON.stringify(filtersWithItemsList)}

                Expected output format:
                {
                  "<attributeUuid>": "<attributeItemUuid>",
                  "<attributeUuid>": "<attributeItemUuid>"
                }
            `;

            try {
                return await geminiService.generateWithRetry(prompt);
            } catch (error) {
                if (error instanceof NotValidJsonStringError) {
                    new MessageNotificator().error(this.$t('alerts.received_not_valid_json'));
                } else if (error instanceof TooManyRequestsError) {
                    new MessageNotificator().error(this.$t('alerts.too_many_requests'));
                } else if (error instanceof ServiceUnavailableError) {
                    new MessageNotificator().error(this.$t('alerts.service_unavailable'));
                } else {
                    new MessageNotificator().error(this.$t('alerts.contact_support'));
                }
                return {};
            }
        },
        formatFilterData(filterUuid, filterItemValue, filtersWithItems) {
            const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

            if (!filterItemValue) return null;

            const filter = filtersWithItems[filterUuid];
            if (!filter) return null;

            const isUuid = uuidRegex.test(filterItemValue);

            if (isUuid) {
                // Look for UUID match
                const foundItem = filter.filterItems.find(item => item.uuid === filterItemValue);
                if (foundItem) {
                    return {
                        items: [{
                            uuid: foundItem.uuid,
                            name: foundItem.name,
                            names: { [this.mainLanguageAlias]: foundItem.name }
                        }]
                    };
                }
            } else {
                // Look for name match
                const foundItem = filter.filterItems.find(item => item.name === filterItemValue);
                if (foundItem) {
                    return {
                        items: [{
                            uuid: foundItem.uuid,
                            name: foundItem.name,
                            names: { [this.mainLanguageAlias]: foundItem.name }
                        }]
                    };
                }
            }

            // No match found
            return null;
        },
    },
    computed: {
        allValueIsChecked() {
            return this.checkedFilters.length === this.allFilters.length && this.allFilters.length > 0
        }
    },
}
</script>